<template>
    <div ref="outerElementRef" class="clear-storage">
        <div class="clear-storage__title">{{ topTitle }}</div>
        <div class="clear-storage__quota">{{ txtTotal }}</div>
        <div class="clear-storage__quota">{{ txtUsage }}</div>
        <button 
            id="btnClearCachePage" 
            ref="button" 
            class="btn clear-storage__button" 
            @click="clearCache"
            @focus="onFocus"
            @blur="onBlur"
        >
            {{ btnTxt }}
        </button>
        <div v-if="isProccessing" class="clear-storage__loader">
            <loader class="loader_bigger loader_default-color" />
        </div> 
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { useApplicationStore } from "@/stores/Application";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";

    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing, 
        setTooltipeMessage
    } = ApplicationStore;

    const router = useRouter();
    const { $domains } = useNuxtApp();
    const { t } = useI18n();
    const { $navigator } = useNuxtApp();
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('ClearCachePage');
    const quotaMB: Ref<string> = ref('0');
    const usageMB: Ref<string> = ref('0');
    const topTitle: string = t(Strings.SETTINGS_CLEAR_CACHE_TITLE).toString();
    const btnTxt: string  = t(Strings.SETTINGS_CLEAR_CACHE_BTN_CAPTION).toString();
    const txtTotal = computed((): string | null =>`${t(Strings.SETTINGS_CLEAR_CACHE_TOTAL).toString()}: ${quotaMB.value} ${t(Strings.SETTINGS_CLEAR_CACHE_MB).toString()}`);
    const txtUsage = computed((): string | null =>`${t(Strings.SETTINGS_CLEAR_CACHE_USAGE).toString()}: ${isClearedStorage.value 
        ? 0
        : usageMB.value} ${t(Strings.SETTINGS_CLEAR_CACHE_MB).toString()}`);

    const isRegKeyListeners: Ref<boolean> = ref(false);
    const button: Ref<HTMLButtonElement | null> = ref(null);
    const isClearedStorage = computed((): boolean => Number(usageMB.value) < $domains.idbCacher.getQuotaLimitInMb());
    const isProccessing: Ref<boolean> = ref(false);

    const goToBack = (): void => {
        router.push(linkItems.settings.routerPath);
    }

    const getStorageQuota = (): void => {
        $domains.idbCacher.getEstimate().then((estimate: StorageEstimate) => {
            if(estimate.quota) {
                quotaMB.value = (estimate.quota / (1024 * 1024)).toFixed(2);
            }
            if(estimate.usage) {
                usageMB.value = (estimate.usage / (1024 * 1024)).toFixed(2);
            }
        }).catch((error: Error) => {
            console.error(error);
        });
    };
    const clearCache = (): void =>{
        if(isClearedStorage.value) {
            return;
        }
        isProccessing.value = true;
        $domains.idbCacher.deleteDBase().then((): void => {
            setTimeout((): void => {
                getStorageQuota();
                isProccessing.value = false;
            }, 1000);
        }).catch((error: Error) => {
            console.error(error);
            setTooltipeMessage(t(Strings.SETTINGS_CLEAR_CACHE_TOOLTIPE_MSG).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
            isProccessing.value = false;
        });
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:left", goToBack);
            $navigator.on("keypress:back", goToBack);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:left", goToBack);
            $navigator.off("keypress:back", goToBack);
            isRegKeyListeners.value = false;
        }
    };

    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onBlur = (): void => {
        unregisterKeyListeners();
    };

    const setSelfFocused = (): void => {
        if (button.value) {
            button.value.focus();
        }
        
        setFocused();
    };
    const unsetSelfFocused = (): void => {
        unregisterKeyListeners();
        unsetFocused();
    };

    onMounted(() => {
        setSelfFocused();
        getStorageQuota();
    });

    onBeforeUnmount(() => {
        unsetSelfFocused();
    });
</script>


<style lang="scss">
    @import url("styles.scss");
</style>